import { DataModel } from "../../base/index.js";
import type { DnsRecordTxtData } from "./types.js";
import {
  DnsRecordSettings,
  type DnsRecordSettingsData,
} from "../DnsRecordSettings/index.js";
import type { DnsZoneCommon } from "../DnsZone/index.js";
import { DnsZone } from "../DnsZone/index.js";
import type { Project } from "../../project/index.js";

export class DnsRecordTxt extends DataModel<DnsRecordTxtData> {
  public readonly dnsZone: DnsZoneCommon;
  public readonly entries?: string[];
  public readonly settings?: DnsRecordSettings;
  public constructor(dnsZone: DnsZoneCommon, data: DnsRecordTxtData) {
    super(data);
    this.dnsZone = dnsZone;
    if ("entries" in data) {
      this.entries = data.entries;
      this.settings = new DnsRecordSettings(data.settings);
    }
  }

  public getTtl(): number | "auto" {
    if (this.settings) {
      return this.settings.ttl;
    }
    return "auto";
  }

  public async addEntry(
    project: Project,
    entry: string,
    settings: DnsRecordSettingsData,
    prefix?: string,
  ) {
    if (prefix) {
      const domain = `${prefix}.${this.dnsZone.domain}`;
      const existingZones = await DnsZone.query(project, { domain }).execute();
      const existingZone = existingZones.items.find((i) => i.domain === domain);

      if (!existingZone) {
        const newZone = await DnsZone.create(prefix, this.dnsZone.id);
        await newZone.setTxtRecord([entry], settings);
        return;
      }
      const existing = existingZone.recordSet.txt.entries ?? [];
      await existingZone.setTxtRecord([...existing, entry], settings);
      return;
    }
    const existingEntries = this.dnsZone.recordSet.txt.entries ?? [];
    await this.dnsZone.setTxtRecord([entry, ...existingEntries], settings);
  }

  public async removeEntry(entry: string) {
    const existingItems = this.dnsZone.recordSet.txt.entries ?? [];
    if (
      this.dnsZone.recordSet.txt.entries &&
      this.dnsZone.recordSet.txt.settings
    ) {
      await this.dnsZone.setTxtRecord(
        existingItems.filter((i) => i === entry),
        this.dnsZone.recordSet.txt.settings.data,
      );
    }
  }

  public async updateEntry(
    entry: string,
    newEntry: string,
    settings: DnsRecordSettingsData,
  ) {
    const existingItems = this.dnsZone.recordSet.txt.entries ?? [];
    const update = existingItems.map((i) => (i === entry ? newEntry : i));
    await this.dnsZone.setTxtRecord(update, settings);
  }
}
